import type { UserStatusHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserStatus/user_status.interface";
import { computed, watch } from "vue";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { useLocationStore } from "../LocationStore/useLocationStore";
import { useSharedStore } from "../SharedStore/useSharedStore";
import { PermissionService } from "./PermissionService";
import { AccessGrantVoter } from "./Voters/AccessGrantVoter";
import { AccessInvitationVoter } from "./Voters/AccessInvitationVoter";
import { ActivityCommentVoter } from "./Voters/ActivityCommentVoter";
import { AdminVoter } from "./Voters/AdminVoter";
import { AppCredentialVoter } from "./Voters/AppCredentialVoter";
import { AppDeveloperProfileShareVoter } from "./Voters/AppDeveloperProfileShareVoter";
import { AppSubscriptionShareVoter } from "./Voters/AppSubscriptionShareVoter";
import { AppSubscriptionVoter } from "./Voters/AppSubscriptionVoter";
import { AppVoter } from "./Voters/AppVoter";
import { AutomationVoter } from "./Voters/AutomationVoter";
import { ClientRoleVoter } from "./Voters/ClientRoleVoter";
import { ClientVoter } from "./Voters/ClientVoter";
import { DescriptionHintVoter } from "./Voters/DescriptionHintVoter";
import { DeveloperAccessRequestVoter } from "./Voters/DeveloperAccessRequestVoter";
import { DeviceActionVoter } from "./Voters/DeviceActionVoter";
import { ExportVoter } from "./Voters/ExportVoter";
import { GeneralLedgerVoter } from "./Voters/GeneralLedgerVoter";
import { LabelVoter } from "./Voters/LabelVoter";
import { LegacyNotificationUserSubscriptionVoter } from "./Voters/LegacyNotificationUserSubscriptionVoter";
import { LocationVoter } from "./Voters/LocationVoter";
import { MediaObjectMetaDataVoter } from "./Voters/MediaObjectMetaDataVoter";
import { NotificationSettingVoter } from "./Voters/NotificationSettingVoter";
import { OAuthScopeVoter } from "./Voters/OAuthScopeVoter";
import { PermissionTemplateGroupVoter } from "./Voters/PermissionTemplateGroupVoter";
import { PinVoter } from "./Voters/PinVoter";
import { PriceQuoteVoter } from "./Voters/PriceQuoteVoter";
import { ProductInventoryVoter } from "./Voters/ProductInventoryVoter";
import { ProductVoter } from "./Voters/ProductVoter";
import { PropertyGroupVoter } from "./Voters/PropertyGroupVoter";
import { PropertyRouteVoter } from "./Voters/PropertyRouteVoter";
import { PropertyVoter } from "./Voters/PropertyVoter";
import { ReportFieldVoter } from "./Voters/ReportFieldVoter";
import { ReportTopicVoter } from "./Voters/ReportTopicVoter";
import { ReportViewVoter } from "./Voters/ReportViewVoter";
import { ReportVoter } from "./Voters/ReportVoter";
import { ReservationVoter } from "./Voters/ReservationVoter";
import { ShortcutVoter } from "./Voters/ShortcutVoter";
import { SortVoter } from "./Voters/SortVoter";
import { TaskFieldVoter } from "./Voters/TaskFieldVoter";
import { TaskProductVoter } from "./Voters/TaskProductVoter";
import { TaskTimeEntryVoter } from "./Voters/TaskTimeEntryVoter";
import { TaskViewVoter } from "./Voters/TaskViewVoter";
import { TaskVoter } from "./Voters/TaskVoter";
import { TranslationContextVoter } from "./Voters/TranslationContextVoter";
import { UserGroupVoter } from "./Voters/UserGroupVoter";
import { UserPermissionVoter } from "./Voters/UserPermissionVoter";
import { UserPreferenceVoter } from "./Voters/UserPreferenceVoter";
import { UserVoter } from "./Voters/UserVoter";
import { WarehouseVoter } from "./Voters/WarehouseVoter";

export const permissionService = new PermissionService({
  setUserPermissions: () => useAuthStore().user.value?.permissions ?? [],
  setUserIri: () => useAuthStore().user.value?.["@id"] ?? null,
  setMode: () => useLocationStore().mode.value,
  setDefaultLocation: () => useLocationStore().modeSelectedLocation.value,
  setDefaultClient: () => useLocationStore().modeSelectedClient.value,
  setLocationClientMap: () => {
    const { locationsList } = useLocationStore();
    return locationsList.value.reduce((map, location) => {
      map.set(location["@id"], location.client);
      return map;
    }, new Map<string, string>());
  },
  setUsersData: () => {
    const { usersList } = useSharedStore();
    return usersList.value.reduce((map, user) => {
      map.set(user["@id"], { statuses: user.statuses });
      return map;
    }, new Map<string, { statuses: UserStatusHydraItem[] }>());
  },
});

export const accessGrantVoter = new AccessGrantVoter(permissionService);
export const accessInvitationVoter = new AccessInvitationVoter(permissionService);
export const clientRoleVoter = new ClientRoleVoter(permissionService);
export const appSubscriptionVoter = new AppSubscriptionVoter(permissionService);
export const appCredentialVoter = new AppCredentialVoter(permissionService);
export const appSubscriptionShareVoter = new AppSubscriptionShareVoter(permissionService);
export const appDeveloperProfileShareVoter = new AppDeveloperProfileShareVoter(permissionService);
export const oauthScopeVoter = new OAuthScopeVoter(permissionService);
export const developerAccessRequestVoter = new DeveloperAccessRequestVoter(permissionService);
export const appVoter = new AppVoter(permissionService);
export const automationVoter = new AutomationVoter(permissionService);
export const exportVoter = new ExportVoter(permissionService);
export const locationVoter = new LocationVoter(permissionService);
export const permissionTemplateGroupVoter = new PermissionTemplateGroupVoter(permissionService);
export const propertyGroupVoter = new PropertyGroupVoter(permissionService);
export const propertyVoter = new PropertyVoter(permissionService);
export const propertyRouteVoter = new PropertyRouteVoter(permissionService);
export const shortcutVoter = new ShortcutVoter(permissionService);
export const adminVoter = new AdminVoter(permissionService);
export const sortVoter = new SortVoter(permissionService);
export const userGroupVoter = new UserGroupVoter(permissionService);
export const userVoter = new UserVoter(permissionService);
export const taskVoter = new TaskVoter(permissionService);
export const reportVoter = new ReportVoter(permissionService);
export const reportTopicVoter = new ReportTopicVoter(permissionService);
export const taskViewVoter = new TaskViewVoter(permissionService);
export const reportViewVoter = new ReportViewVoter(permissionService);
export const pinVoter = new PinVoter(permissionService);
export const descriptionHintVoter = new DescriptionHintVoter(permissionService);
export const taskTimeEntryVoter = new TaskTimeEntryVoter(permissionService, taskVoter);
export const taskProductVoter = new TaskProductVoter(permissionService, taskVoter);
export const activityCommentVoter = new ActivityCommentVoter(permissionService, taskVoter, reportVoter);
export const mediaObjectMetaDataVoter = new MediaObjectMetaDataVoter(permissionService, taskVoter, reportVoter);
export const taskFieldVoter = new TaskFieldVoter(permissionService, taskVoter);
export const reportFieldVoter = new ReportFieldVoter(permissionService, reportVoter);
export const reservationVoter = new ReservationVoter(permissionService);
export const legacyNotificationUserSubscriptionVoter = new LegacyNotificationUserSubscriptionVoter(permissionService);
export const notificationSettingVoter = new NotificationSettingVoter(permissionService);
export const userPermissionVoter = new UserPermissionVoter(permissionService);
export const userPreferenceVoter = new UserPreferenceVoter(permissionService);
export const productVoter = new ProductVoter(permissionService);
export const productInventoryVoter = new ProductInventoryVoter(permissionService);
export const generalLedgerVoter = new GeneralLedgerVoter(permissionService);
export const priceQuoteVoter = new PriceQuoteVoter(permissionService);
export const clientVoter = new ClientVoter(permissionService);
export const deviceActionVoter = new DeviceActionVoter(permissionService);
export const labelVoter = new LabelVoter(permissionService);
export const warehouseVoter = new WarehouseVoter(permissionService);
export const translationContextVoter = new TranslationContextVoter(permissionService);
export const isAdmin = computed(() => adminVoter.isAdmin());

export function usePermsWatch() {
  watch([
    () => useAuthStore().isAuthenticated.value,
    () => useAuthStore().user.value?.permissions,
    () => useLocationStore().selectedLocation.value,
  ], ([isAuthenticated, authPermissions, selectedLocation]) => {
    if (!isAuthenticated) {
      console.log("%c [usePermsWatch] not authenticated", "color: #FFA500; font-weight: bold;");
      return;
    }
    if (!authPermissions) {
      console.log("%c [usePermsWatch] no permissions", "color: #FFA500; font-weight: bold;");
      return;
    }
    if (!selectedLocation) {
      console.log("%c [usePermsWatch] no location", "color: #FFA500; font-weight: bold;");
      return;
    }

    console.log("%c [usePermsWatch] init", "color: #4CAF50; font-weight: bold;");
    permissionService.init();
  }, {
    immediate: true,
    deep: true,
  });
}
